import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface PrivacySection {
  title: string;
  content: string;
}

export interface PrivacySettings {
  pageTitle: string;
  pageSubtitle: string;
  lastUpdated: string;
  sections: PrivacySection[];
}

export const defaultPrivacySettings: PrivacySettings = {
  pageTitle: 'Política de Privacidade',
  pageSubtitle: 'Última atualização: junho de 2026',
  lastUpdated: '12 de junho de 2026',
  sections: [
    {
      title: '1. Informações que coletamos',
      content: 'Ao acessar o Miaudecasa, podemos coletar automaticamente informações como endereço IP, tipo de navegador, páginas visitadas e tempo de permanência. Quando você se inscreve na nossa newsletter, coletamos seu nome e endereço de e-mail. Quando você interage com nossos artigos (curtidas e reações), armazenamos essas interações de forma anônima para melhorar nosso conteúdo.',
    },
    {
      title: '2. Como utilizamos essas informações',
      content: 'Utilizamos as informações coletadas para: melhorar a experiência de navegação no site, personalizar o conteúdo exibido, enviar nossa newsletter com conteúdo relevante sobre o universo felino (apenas para quem se inscreveu), analisar quais artigos têm melhor desempenho para produzir mais conteúdo de qualidade.',
    },
    {
      title: '3. Cookies',
      content: 'Utilizamos cookies essenciais para o funcionamento do site e cookies de análise para entender como os visitantes interagem com nosso conteúdo. Você pode desabilitar os cookies nas configurações do seu navegador, mas isso pode afetar algumas funcionalidades do site.',
    },
    {
      title: '4. Compartilhamento de dados',
      content: 'Não vendemos, trocamos ou transferimos suas informações pessoais para terceiros. Podemos compartilhar dados agregados e anônimos para fins de análise de audiência. Em caso de obrigação legal, podemos divulgar informações mediante ordem judicial.',
    },
    {
      title: '5. Seus direitos',
      content: 'Você tem direito a: acessar seus dados pessoais armazenados, solicitar a correção de dados incompletos ou incorretos, solicitar a exclusão dos seus dados, cancelar o recebimento da newsletter a qualquer momento através do link de descadastro presente em cada e-mail.',
    },
    {
      title: '6. Segurança',
      content: 'Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações contra acesso não autorizado, alteração, divulgação ou destruição. Utilizamos criptografia SSL em todas as comunicações e armazenamos dados em servidores seguros.',
    },
    {
      title: '7. Alterações nesta política',
      content: 'Podemos atualizar esta Política de Privacidade periodicamente. Recomendamos que você revise esta página regularmente para se manter informado sobre como protegemos suas informações. A data da última atualização estará sempre indicada no topo desta página.',
    },
    {
      title: '8. Contato',
      content: 'Se você tiver dúvidas sobre esta Política de Privacidade ou sobre como tratamos seus dados, entre em contato conosco através do e-mail contato@miaudecasa.com.br ou pelo formulário na página Fale Conosco.',
    },
  ],
};

function mapRow(row: Record<string, unknown> | null): PrivacySettings {
  if (!row) return JSON.parse(JSON.stringify(defaultPrivacySettings));

  const parseJson = (val: unknown, fallback: unknown) => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'object') return val;
    try {
      return JSON.parse(val as string);
    } catch {
      return fallback;
    }
  };

  return {
    pageTitle: (row.page_title as string) ?? defaultPrivacySettings.pageTitle,
    pageSubtitle: (row.page_subtitle as string) ?? defaultPrivacySettings.pageSubtitle,
    lastUpdated: (row.last_updated as string) ?? defaultPrivacySettings.lastUpdated,
    sections: parseJson(row.sections, defaultPrivacySettings.sections) as PrivacySection[],
  };
}

export function usePrivacySettings() {
  const [settings, setSettings] = useState<PrivacySettings>(defaultPrivacySettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('privacy_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();

      if (error) throw error;
      setSettings(mapRow(data));
    } catch {
      // keep defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async (updated: PrivacySettings): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('privacy_settings')
        .upsert({
          id: 1,
          page_title: updated.pageTitle,
          page_subtitle: updated.pageSubtitle,
          last_updated: updated.lastUpdated,
          sections: updated.sections,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      setSettings(updated);
      return true;
    } catch {
      return false;
    }
  };

  return { settings, saveSettings, loading };
}